<template>
    <VSelect
        v-model="internalValue"
        :class="classList"
        v-bind="configuration"
        :rules="validationRules"
        :disabled="disabled"
        :attach="true"
        v-on="$listeners"
    >
        <template #label>
            <SrInputLabel :label="label" :required="required" />
        </template>
        <template #prepend-item>
            <div>
                <SrSelectSearch v-if="searchable" v-model="searchValue" @keyup="onSearchKeyUp" />

                <SrSelectToggle
                    v-if="bulkSelect && multiple"
                    :toggle-button-text="toggleButtonText"
                    :all-items-selected="allItemsSelected"
                    @toggle="toggleAll"
                />
            </div>
        </template>

        <template #selection="{ item, index, itemDisabled }">
            <slot name="selection" v-bind="{ item, index, itemDisabled }">
                {{ getItemLabel(item) }}{{ getSeparatorForMultipleSelection(index) }}
            </slot>
        </template>

        <template #item="{ item, on }">
            <slot name="item" v-bind="{ item, on }">
                <SrSelectItem
                    v-model="internalValue"
                    :item="item"
                    :multiple="multiple"
                    :item-text="itemText"
                    :item-value="itemValue"
                    :bulk-select="bulkSelect"
                    :return-object="returnObject"
                >
                    <!-- pass slots to child component -->
                    <slot v-for="(_, name) in $scopedSlots" :slot="name" :name="name" v-bind="{ item }" />
                </SrSelectItem>
            </slot>
        </template>
    </VSelect>
</template>

<script>
// https://vuetifyjs.com/en/components/selects#selects

import { USER_INPUT_CLASS } from '../../utils/classNames';
import PropagateChangeEvent from '../../mixins/PropagateChangeEvent';
import SrSelectSearch from './SrSelectSearch';
import SrSelectToggle from './SrSelectToggle';
import { SrInputLabel } from '../SrInputLabel';
import SrSelectItem from './SrSelectItem';

const ELEMENT_CLASS = 'sr-select';

export default {
    name: 'SrSelect',
    components: {
        SrSelectItem,
        SrInputLabel,
        SrSelectSearch,
        SrSelectToggle,
    },
    mixins: [PropagateChangeEvent],
    model: {
        prop: 'value',
        event: 'change',
    },
    props: {
        // eslint-disable-next-line vue/require-default-prop
        value: {
            type: [Object, String, Array, Number],
        },
        itemText: {
            type: String,
            default: 'label',
        },
        itemValue: {
            type: String,
            default: 'value',
        },
        returnObject: {
            type: Boolean,
            default: false,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        searchable: {
            type: Boolean,
            default: false,
        },
        sortOrder: {
            type: String,
            default: null,
            validator(value) {
                return ['ASC', 'DESC'].includes(value);
            },
        },
        searchableProperty: {
            type: String,
            default: '',
        },
        items: {
            type: Array,
            required: true,
        },
        bulkSelect: {
            type: Boolean,
            default: false,
        },
        selectAllText: {
            type: String,
            default: 'Select All',
        },
        deselectAllText: {
            type: String,
            default: 'Deselect All',
        },
        required: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        label: {
            type: String,
            default: '',
        },
        rules: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            searchValue: '',
            internalValue: this.value,
        };
    },
    computed: {
        configuration() {
            return {
                items: this.sortedItems,
                multiple: this.multiple,
                itemText: this.itemText,
                itemValue: this.itemValue,
                returnObject: this.returnObject,
                itemDisabled: 'disabled',
                outlined: true,
                menuProps: { bottom: true, offsetY: true },
                ...this.$attrs,
            };
        },

        sortedItems() {
            if (!this.sortOrder) {
                return this.filteredItems;
            }
            return [...this.filteredItems].sort((left, right) => {
                const leftLabel = this.getItemLabel(left);
                const rightLabel = this.getItemLabel(right);

                if (this.sortOrder === 'ASC') {
                    return leftLabel.localeCompare(rightLabel);
                }
                return rightLabel.localeCompare(leftLabel);
            });
        },

        filteredItems() {
            if (!this.searchValue) {
                return this.items;
            }

            const lowerCasedSearchValue = this.searchValue.toLowerCase();

            const itemTextMatchesSearchCriteria = (item) =>
                this.getItemSearchValue(item).toLowerCase().includes(lowerCasedSearchValue);

            return this.items.filter(itemTextMatchesSearchCriteria);
        },

        allItemsSelected() {
            return this.multiple && this.internalValue && this.internalValue.length === this.availableItems.length;
        },

        availableItems() {
            return this.items.filter((item) => !this.isItemDisabled(item));
        },

        availableItemValues() {
            return this.availableItems.map(this.getItemValue);
        },

        toggleButtonText() {
            return this.allItemsSelected ? this.deselectAllText : this.selectAllText;
        },

        validationRules() {
            const rules = [...this.rules];
            const noEmptyValueRule = (value) => {
                if (value === null || value === '') {
                    return true;
                }
                return Array.isArray(value) && !value.length;
            };
            const isRequiredRule = (value) => (noEmptyValueRule(value) ? "Input can't be empty" : true);

            if (this.required) {
                rules.unshift(isRequiredRule);
            }
            return rules;
        },

        classList() {
            return {
                [ELEMENT_CLASS]: true,
                [USER_INPUT_CLASS]: true,
                multiple: this.multiple,
                single: !this.multiple,
            };
        },
    },
    watch: {
        value(newValues) {
            this.internalValue = newValues;
        },
    },
    created() {
        this.initDefaultValue();
        if (this.items.some((item) => item.children) && !this.multiple) {
            throw new Error('Children are not supported for single select dropdowns');
        }
    },

    methods: {
        initDefaultValue() {
            if (this.multiple) {
                this.internalValue = [];
            }
            if (this.value) {
                this.internalValue = this.value;
            }
        },
        updateValue(value) {
            this.internalValue = value;
            this.emitChange(value);
        },

        isItemSelected(item) {
            const itemValue = this.getItemValue(item);

            if (this.multiple) {
                return this.internalValue.includes(itemValue);
            }

            return this.getItemValue(this.internalValue) === itemValue;
        },

        toggleAll() {
            if (!this.multiple) {
                throw Error('Functionality is only supported if select type is multiple!');
            }

            if (this.allItemsSelected) {
                this.emitChange([]);
            } else {
                this.emitChange(this.availableItemValues);
            }
        },

        getItemLabel(item) {
            if (typeof item === 'string') {
                return item;
            }
            return item[this.itemText];
        },

        getItemSearchValue(item) {
            if (typeof item === 'string') {
                return item;
            }
            return item[this.searchableProperty || this.itemText];
        },

        getItemValue(item) {
            if (typeof item === 'string') {
                return item;
            }
            return item[this.itemValue];
        },

        isItemDisabled(item) {
            if (typeof item === 'object' && Object.prototype.hasOwnProperty.call(item, 'disabled')) {
                return item.disabled;
            }

            return false;
        },

        onSearchKeyUp(event) {
            this.$emit('search-keyup', event);
        },

        getSeparatorForMultipleSelection(index) {
            return this.multiple && index !== this.internalValue.length - 1 ? ', ' : '';
        },
    },
};
</script>
